import React, { useCallback, type SyntheticEvent, type MouseEvent, useMemo } from 'react';
import { xcss, Pressable } from '@atlaskit/primitives';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { asyncInlineCreateStatusModal } from '@atlassian/jira-inline-create-status-modal/entrypoint.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { defaultModalProps } from '@atlassian/jira-issue-status-and-types-common/src/index.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	LIST_VIEW_NO_RESULTS_SOURCE,
	LIST_VIEW_SOURCE,
} from '@atlassian/jira-issue-status-and-types-common/src/types.tsx';
import { createStatusExperience } from '../../../../experiences.tsx';
import messages from './messages.tsx';
import type { CreateStatusButtonProps } from './types.tsx';

export const CreateStatusButton = ({
	statusNames,
	onCreateStatusSuccess,
	projectId,
	projectKey,
	projectType,
	issueTypeId,
	isNoResultsView,
	buttonRef = null,
}: CreateStatusButtonProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();

	const handleClick = useCallback(
		(e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			if (!isNoResultsView) {
				fireUIAnalytics(analyticsEvent, 'createStatusButton', { source: LIST_VIEW_SOURCE });
			}

			// Because we are triggering the click manually for keyboard users
			// this handler becomes the source-of-truth handler for both keyboard users and mouse users
			// However, we need to stopPropagation from here to prevent double modal for mouse users
			// When mouse user clicks the button, if we don't stop propagation here, handleTransitionChange
			// will be called which will trigger click manually again and cause double modal rendering
			e.stopPropagation();
		},
		[isNoResultsView],
	);

	const handleMouseDown = useCallback(
		(e: MouseEvent) =>
			e.button === 0 &&
			isNoResultsView &&
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'createStatusButton',
				{ source: LIST_VIEW_NO_RESULTS_SOURCE },
			),
		[createAnalyticsEvent, isNoResultsView],
	);

	const onModalClose = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'closed',
				actionSubject: 'inlineCreateStatusModal',
			}),
		);
	}, [createAnalyticsEvent]);

	const entryPointProps = useMemo(
		() => ({
			statusNames,
			projectId,
			projectKey,
			projectType,
			onClose: onModalClose,
			isNewControllerEnabled: true,
			workflowAssociatedIssueTypeId: issueTypeId,
			onStatusCreated: onCreateStatusSuccess,
			createStatusExperience,
			source: isNoResultsView ? LIST_VIEW_NO_RESULTS_SOURCE : LIST_VIEW_SOURCE,
		}),
		[
			isNoResultsView,
			issueTypeId,
			onCreateStatusSuccess,
			onModalClose,
			projectId,
			projectKey,
			projectType,
			statusNames,
		],
	);

	const entryPointParams = useMemo(
		() => ({
			cloudId,
			projectKey,
		}),
		[cloudId, projectKey],
	);

	return (
		<ModalEntryPointPressableTrigger
			entryPoint={asyncInlineCreateStatusModal}
			entryPointProps={entryPointProps}
			entryPointParams={entryPointParams}
			interactionName="create-status-modal"
			modalProps={defaultModalProps}
			forwardedRef={buttonRef}
			useInternalModal
		>
			{({ ref }) => (
				<Pressable
					onClick={handleClick}
					onMouseDown={handleMouseDown}
					backgroundColor="color.background.neutral.subtle"
					xcss={isNoResultsView ? createStatusInlineNoResultStyles : createStatusInlineStyles}
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					ref={ref as React.Ref<HTMLButtonElement>}
				>
					{formatMessage(messages.createStatus)}
				</Pressable>
			)}
		</ModalEntryPointPressableTrigger>
	);
};

const createStatusInlineNoResultStyles = xcss({
	paddingInline: 'space.150',
	color: 'color.link',
	fontWeight: 'font.weight.bold',
});

const createStatusInlineStyles = xcss({
	display: 'flex',
	width: '100%',
	padding: 'space.150',
	color: 'color.text',
	fontWeight: 'font.weight.medium',
});
